import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { recentSeasons, currentSeason } from "../../utils/seasons";
import { 
  Calendar, Plus, Pencil, Trash2, Loader2, RefreshCw, 
  Eye, EyeOff, Play, Square, Clock, CheckCircle, XCircle, Trophy
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Switch } from "../ui/switch";
import FixturesBulkPaste from "./FixturesBulkPaste";

const TEAMS = ["First Team", "Women", "U21", "U18"];
const TEAM_LABELS = {
  "First Team": "Men's First Team",
  "Women": "Women's First Team",
  "U21": "Men's U21",
  "U18": "Men's U18",
};
const COMPETITIONS = [
  "Championship", "Premier League", "FA Cup", "EFL Cup", "Europa Conference League",
  "WSL", "FA Cup (Women)", "Women's League Cup",
  "Premier League 2", "Premier League 2 Playoff", "Premier League U18", "U18 Premier League", "U18 Premier League Cup",
  "EFL Trophy Final Stage", "EFL Trophy Southern Grp. D",
  "National League Cup Final Stage", "National League Cup Group C",
  "Club Friendlies"
];
const SEASONS = recentSeasons(5);

const FixturesAdmin = () => {
  const { token } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraperStatus, setScraperStatus] = useState(null);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [filter, setFilter] = useState({ team: "all", season: currentSeason(), status: "all" });
  const [editingFixture, setEditingFixture] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [visibleCount, setVisibleCount] = useState(100);
  const [recalcingPoints, setRecalcingPoints] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  const recalcPredictionPoints = async () => {
    setRecalcingPoints(true);
    setRecalcResult(null);
    try {
      const res = await axios.get(`${API}/score-predictor/admin/update-points`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecalcResult(res.data);
      toast.success(
        `Recalculated points — ${res.data.predictions_updated} predictions across ${res.data.fixtures_processed} fixtures`
      );
    } catch (e) {
      toast.error(`Failed to recalculate: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRecalcingPoints(false);
    }
  };

  // Derive a season string (e.g. "2026-27") from a fixture's date when the
  // season field is missing on legacy rows. July 1 is the cutoff.
  const seasonFromDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const startYear = m >= 7 ? y : y - 1;
    const endYY = String((startYear + 1) % 100).padStart(2, "0");
    return `${startYear}-${endYY}`;
  };

  const emptyFixture = {
    team: "First Team",
    opponent: "",
    venue: "Home",
    home_away: "Home",
    competition: "Championship",
    date: "",
    time: "",
    season: currentSeason(),
    score_home: null,
    score_away: null,
    is_completed: false
  };

  useEffect(() => {
    fetchFixtures();
    fetchScraperStatus();
    fetchSectionStatus();
  }, []);

  const fetchFixtures = async () => {
    try {
      const response = await axios.get(`${API}/fixtures`);
      setFixtures(response.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      toast.error("Failed to fetch fixtures");
    } finally {
      setLoading(false);
    }
  };

  const fetchScraperStatus = async () => {
    try {
      const response = await axios.get(`${API}/fixtures/scraper-status`);
      setScraperStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch scraper status");
    }
  };

  const fetchSectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/section-toggles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.fixtures_section_enabled);
    } catch (error) {
      console.error("Failed to fetch section status");
    }
  };

  const toggleSection = async () => {
    setToggling(true);
    try {
      const response = await axios.post(`${API}/admin/section-toggle/fixtures_section_enabled`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.fixtures_section_enabled);
      toast.success(`Fixtures section ${response.data.fixtures_section_enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle section");
    } finally {
      setToggling(false);
    }
  };

  const runScraper = async () => {
    setScraping(true);
    try {
      const response = await axios.post(`${API}/fixtures/scrape-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Scraper completed");
      fetchFixtures();
      fetchScraperStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Scraper failed");
    } finally {
      setScraping(false);
    }
  };

  const syncAllFixtures = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await axios.post(`${API}/admin/fixtures/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncResult(response.data);
      toast.success(`Sync complete: +${response.data.added} added, ~${response.data.updated} updated`);
      fetchFixtures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const saveFixture = async () => {
    try {
      if (editingFixture.id) {
        await axios.put(`${API}/fixtures/${editingFixture.id}`, editingFixture, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fixture updated");
      } else {
        await axios.post(`${API}/fixtures`, editingFixture, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fixture created");
      }
      setDialogOpen(false);
      setEditingFixture(null);
      fetchFixtures();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save fixture");
    }
  };

  const deleteFixture = async (id) => {
    if (!confirm("Are you sure you want to delete this fixture?")) return;
    try {
      await axios.delete(`${API}/fixtures/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fixture deleted");
      fetchFixtures();
    } catch (error) {
      toast.error("Failed to delete fixture");
    }
  };

  const filteredFixtures = fixtures.filter(f => {
    if (filter.team !== "all" && f.team !== filter.team) return false;
    if (filter.season !== "all") {
      const effectiveSeason = f.season || seasonFromDate(f.date);
      if (effectiveSeason !== filter.season) return false;
    }
    if (filter.status === "pending" && f.is_completed) return false;
    if (filter.status === "completed" && !f.is_completed) return false;
    return true;
  });

  // Reset pagination whenever filters change
  useEffect(() => { setVisibleCount(100); }, [filter.team, filter.season, filter.status]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="fixtures-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Calendar className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          FIXTURES ADMIN
        </h1>
        <Button
          onClick={() => { setEditingFixture({...emptyFixture}); setDialogOpen(true); }}
          className="bg-[#7C2C3B] hover:bg-[#63232F]"
          data-testid="add-fixture-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Fixture
        </Button>
      </div>

      {/* Section Visibility Toggle */}
      <div className={`rounded-xl p-4 mb-6 border-2 ${sectionEnabled ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {sectionEnabled ? <Eye className="w-6 h-6 text-green-600" /> : <EyeOff className="w-6 h-6 text-red-600" />}
            <div>
              <h3 className="font-bold text-slate-800">Fixtures Section Visibility</h3>
              <p className="text-sm text-slate-600">
                {sectionEnabled ? "Section is VISIBLE on the website" : "Section is HIDDEN from the website"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${sectionEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
              {sectionEnabled ? "VISIBLE" : "HIDDEN"}
            </span>
            <Switch
              checked={sectionEnabled}
              onCheckedChange={toggleSection}
              disabled={toggling}
              data-testid="toggle-fixtures-visibility"
            />
          </div>
        </div>
      </div>

      {/* AI Bulk Paste-In Import — pastes any format, LLM parses, admin reviews, all rows marked manually_edited */}
      <FixturesBulkPaste onImported={fetchFixtures} />

      {/* Scraper Control Panel */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <RefreshCw className="w-5 h-5" /> Fixtures Scraper
            </h3>
            <p className="text-sm text-slate-500">
              {scraperStatus?.last_run 
                ? `Last run: ${new Date(scraperStatus.last_run).toLocaleString()}`
                : "No scraper runs yet"}
            </p>
            {scraperStatus?.scheduler_running && (
              <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                <Clock className="w-3 h-3" /> Auto-runs every 24 hours
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${scraperStatus?.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
            <span className="text-sm text-slate-600">
              {scraperStatus?.scheduler_running ? "Running" : "Idle"}
            </span>
            <Button 
              onClick={runScraper} 
              disabled={scraping}
              className="ml-4 bg-[#2BAEE5] hover:bg-[#228BB7]"
              data-testid="run-scraper-btn"
            >
              {scraping ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scraping...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Run Now</>
              )}
            </Button>
          </div>
        </div>
        {scraperStatus?.last_result && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xl font-bold text-[#0F172A]">{scraperStatus.last_result.total_fixtures || 0}</div>
              <div className="text-xs text-slate-500">Total Found</div>
            </div>
            <div>
              <div className="text-xl font-bold text-green-600">{scraperStatus.last_result.new_fixtures || 0}</div>
              <div className="text-xs text-slate-500">New Added</div>
            </div>
            <div>
              <div className="text-xl font-bold text-blue-600">{scraperStatus.last_result.updated_fixtures || 0}</div>
              <div className="text-xs text-slate-500">Updated</div>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-500">{scraperStatus.last_result.skipped || 0}</div>
              <div className="text-xs text-slate-500">Skipped</div>
            </div>
          </div>
        )}
      </div>

      {/* FotMob Sync Panel - All Teams */}
      <div className="bg-blue-50 rounded-xl p-4 mb-6 border-2 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" /> Sync All Teams (FotMob)
            </h3>
            <p className="text-sm text-slate-500">
              Fetch fixtures for Men, Women, U21, U18 including all cup competitions. Auto-runs daily.
            </p>
          </div>
          <Button 
            onClick={syncAllFixtures} 
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="sync-all-fixtures-btn"
          >
            {syncing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Sync All Teams</>
            )}
          </Button>
        </div>
        {syncResult && (
          <div className="mt-3 pt-3 border-t border-blue-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {Object.entries(syncResult.teams || {}).map(([team, stats]) => (
                <div key={team} className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs font-medium text-blue-600 uppercase mb-1">{TEAM_LABELS[team] || team}</div>
                  <div className="text-lg font-bold text-slate-800">{stats.total}</div>
                  <div className="text-xs text-slate-500">
                    <span className="text-green-600">+{stats.added}</span> / <span className="text-blue-600">~{stats.updated}</span>
                  </div>
                </div>
              ))}
            </div>
            {syncResult.competitions && syncResult.competitions.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Competitions found:</p>
                <div className="flex flex-wrap gap-1">
                  {syncResult.competitions.map(c => (
                    <span key={c} className="text-xs bg-white text-blue-700 px-2 py-0.5 rounded border border-blue-200">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Prediction Points Recalculation */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-600" /> Recalculate Prediction Points
            </h3>
            <p className="text-sm text-slate-500">
              Walks every completed First-Team fixture with a saved score and re-grades every fan prediction. Correct score = 3 pts, correct result = 1 pt. Safe to run any time.
            </p>
          </div>
          <Button
            onClick={recalcPredictionPoints}
            disabled={recalcingPoints}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="recalc-prediction-points-btn"
          >
            {recalcingPoints ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recalculating…</>
            ) : (
              <><Trophy className="w-4 h-4 mr-2" /> Recalculate Points</>
            )}
          </Button>
        </div>
        {recalcResult && (
          <div className="mt-3 pt-3 border-t border-amber-200 grid grid-cols-2 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-amber-600 uppercase mb-1">Fixtures Processed</div>
              <div className="text-lg font-bold text-slate-800" data-testid="recalc-fixtures-count">
                {recalcResult.fixtures_processed}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-xs font-medium text-amber-600 uppercase mb-1">Predictions Updated</div>
              <div className="text-lg font-bold text-slate-800" data-testid="recalc-predictions-count">
                {recalcResult.predictions_updated}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <Select value={filter.team} onValueChange={(v) => setFilter({...filter, team: v})}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Teams</SelectItem>
            {TEAMS.map(t => <SelectItem key={t} value={t}>{TEAM_LABELS[t] || t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.season} onValueChange={(v) => setFilter({...filter, season: v})}>
          <SelectTrigger className="w-32"><SelectValue placeholder="Season" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Seasons</SelectItem>
            {SEASONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v})}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-slate-500 self-center">
          Showing {filteredFixtures.length} of {fixtures.length} fixtures
        </div>
      </div>

      {/* Fixtures Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[500px]">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-100 z-10">
              <TableRow>
                <TableHead className="w-28">Date</TableHead>
                <TableHead className="w-28">Team</TableHead>
                <TableHead>Opponent</TableHead>
                <TableHead className="w-20">H/A</TableHead>
                <TableHead>Competition</TableHead>
                <TableHead className="w-24 text-center">Score</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFixtures.slice(0, visibleCount).map(fixture => (
                <TableRow key={fixture.id} className={!fixture.is_completed ? "bg-amber-50/50" : ""}>
                  <TableCell className="text-sm">{fixture.date}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                      fixture.team === "First Team" ? "bg-[#7C2C3B] text-white" :
                      fixture.team === "Women" ? "bg-purple-600 text-white" :
                      fixture.team === "U21" ? "bg-blue-600 text-white" :
                      "bg-green-600 text-white"
                    }`}>
                      {fixture.team}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{fixture.opponent}</TableCell>
                  <TableCell className="text-center">
                    <span className={`text-xs px-2 py-1 rounded ${fixture.home_away === "Home" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                      {fixture.home_away}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{fixture.competition}</TableCell>
                  <TableCell className="text-center font-bold">
                    {fixture.is_completed ? `${fixture.score_home} - ${fixture.score_away}` : "-:-"}
                  </TableCell>
                  <TableCell>
                    {fixture.is_completed ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs">
                        <CheckCircle className="w-3 h-3" /> FT
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600 text-xs">
                        <Clock className="w-3 h-3" /> Pending
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0"
                        onClick={() => { setEditingFixture({...fixture}); setDialogOpen(true); }}
                        data-testid={`edit-fixture-${fixture.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                        onClick={() => deleteFixture(fixture.id)}
                        data-testid={`delete-fixture-${fixture.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {filteredFixtures.length > visibleCount && (
          <div className="p-4 border-t border-slate-200 flex items-center justify-center bg-slate-50">
            <Button
              variant="outline"
              onClick={() => setVisibleCount(v => v + 100)}
              data-testid="load-more-fixtures-btn"
            >
              Load 100 more ({filteredFixtures.length - visibleCount} remaining)
            </Button>
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingFixture?.id ? "EDIT FIXTURE" : "ADD NEW FIXTURE"}
            </DialogTitle>
          </DialogHeader>
          
          {editingFixture && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Team</Label>
                  <Select value={editingFixture.team} onValueChange={(v) => setEditingFixture({...editingFixture, team: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEAMS.map(t => <SelectItem key={t} value={t}>{TEAM_LABELS[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Home/Away</Label>
                  <Select value={editingFixture.home_away} onValueChange={(v) => setEditingFixture({...editingFixture, home_away: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Home">Home</SelectItem>
                      <SelectItem value="Away">Away</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Opponent</Label>
                <Input 
                  value={editingFixture.opponent} 
                  onChange={(e) => setEditingFixture({...editingFixture, opponent: e.target.value})}
                  placeholder="e.g. Arsenal"
                />
              </div>
              
              <div>
                <Label>Competition</Label>
                <Select value={editingFixture.competition} onValueChange={(v) => setEditingFixture({...editingFixture, competition: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMPETITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date"
                    value={editingFixture.date} 
                    onChange={(e) => setEditingFixture({...editingFixture, date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input 
                    type="time"
                    value={editingFixture.time || ""} 
                    onChange={(e) => setEditingFixture({...editingFixture, time: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Season</Label>
                  <Select value={editingFixture.season} onValueChange={(v) => setEditingFixture({...editingFixture, season: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEASONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Home Score</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingFixture.score_home ?? ""} 
                    onChange={(e) => setEditingFixture({...editingFixture, score_home: e.target.value ? parseInt(e.target.value) : null})}
                  />
                </div>
                <div>
                  <Label>Away Score</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingFixture.score_away ?? ""} 
                    onChange={(e) => setEditingFixture({...editingFixture, score_away: e.target.value ? parseInt(e.target.value) : null})}
                  />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-2 pb-2">
                    <Switch
                      checked={editingFixture.is_completed}
                      onCheckedChange={(v) => setEditingFixture({...editingFixture, is_completed: v})}
                    />
                    <Label className="mb-0">Completed</Label>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveFixture} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-fixture-btn">
              {editingFixture?.id ? "Update" : "Create"} Fixture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FixturesAdmin;
